import {Injectable} from '@angular/core';
import {combineLatest} from 'rxjs';
import {filter, take} from 'rxjs/operators';
import {GGHls} from '../../engine/playable/gghls';
import {HlsjsVod} from '../../engine/playable/hlsjsvod';
import {AnyPlayable, ContentType, MultiBitrate, Playable, Seekable, SeekableLive} from '../../engine/playable/playable';
import {UiToPlayableService} from '../../engine/ui-to-playable.service';
import {Store} from '@ngxs/store';
import {
  ActionTypes, AppendPlayableLog,
  SetCurrentTime,
  SetLoading,
  SetPlayableStats,
  SetPlayingState,
  SetQualities,
  SetQuality,
  SetTotalTime,
  SetVideoType
} from '../../store/player.actions';
import {Hlsjs} from '../../engine/playable/hlsjs';

@Injectable()
export class PlayableFactoryService {
  private playable: Playable;

  constructor(private proto: UiToPlayableService, private store: Store) {
  }

  async setupVideo(video: HTMLVideoElement): Promise<Playable> {
    this.proto.cleanUp();

    const contentType = await this.store.select(state => state.player.contentType).pipe(take(1)).toPromise();
    const source = await this.store.select(state => state.player.src).pipe(take(1)).toPromise();
    const autoplay = await this.store.select(state => state.player.autoplay).pipe(take(1)).toPromise();

    const playable = await this.setupTypedVideo(contentType, source, video);

    //console.log('setupVideo:', playable);

    if (autoplay) {
      playable.play();
    }

    return playable;
  }

  async setupTypedVideo(type: ContentType, src: string, video: HTMLVideoElement): Promise<Playable> {
    const playable = await this.getPlayable(type, src);
    this.playable = playable;

    this.setupPlayable(playable);
    this.setupSeekable(playable);
    this.setupMulibitrate(playable);

    // console.log('setupTypedVideo', type, src);

    playable.attachMedia(video);
    playable.loadSource(src);

    this.store.select(state => state.player.volume).pipe(filter(v => isFinite(v))).subscribe(value => this.setVolume(value));

    this.store.dispatch(new SetVideoType({
      seekable: (<Seekable>playable).isSeekable,
      live: (<SeekableLive>playable).isLive
    }));

    return playable;
  }

  async getPlayable(type: ContentType, src: string): Promise<AnyPlayable> {
    //console.log('getPlayable', type);

    // if (type === ContentType.MP4) return new Mp4();
    if (type === ContentType.VOD) {
      return new HlsjsVod();
    }

    // if (await GGHlsDvr.isAvailable(src)) {
    //   return new GGHlsDvr();
    // } else {
    return new GGHls();
    // }
  }

  getCurrentPlayable() {
    return this.playable;
  }

  setupPlayable(playable) {
    playable.playingState$.subscribe(state => this.store.dispatch(new SetPlayingState({state})));
    playable.loading$.subscribe(value => this.store.dispatch(new SetLoading({loading: value})));
    playable.stats$.subscribe(value => this.store.dispatch(new SetPlayableStats(value)));
    playable.log$.subscribe(value => this.store.dispatch(new AppendPlayableLog(value)));

    // if (playable.isLive) {
    //   console.log(playable);
    //   (playable as Hlsjs).log$.subscribe((...args) => console.log(args[0], args[1]));
    // }

    this.proto.onUiEvent(ActionTypes.TOGGLE_PLAY).subscribe(() => this.play());
    this.proto.onUiEvent(ActionTypes.SEEK_TO).subscribe(({position}) => this.seekTo(position));
    this.proto.onUiEvent(ActionTypes.SEEK_TO_LIVE).subscribe(() => this.seekToLive());
  }

  setupSeekable(playable: Playable) {
    const seekable = playable as Seekable;

    console.log({isSeekable: seekable.isSeekable});

    if (seekable.isSeekable) {
      seekable.currentTime$.subscribe(value => this.store.dispatch(new SetCurrentTime({value})));
      seekable.totalTime$.subscribe(value => this.store.dispatch(new SetTotalTime({value})));
    }
  }

  setupMulibitrate(playable: Playable) {
    const mb = playable as MultiBitrate;
    if (mb.qualities$) {
      mb.qualities$.subscribe(qualities => this.store.dispatch(new SetQualities({qualities})));
      mb.currentQuality$.subscribe(quality => this.store.dispatch(new SetQuality({quality})));

      // SAVED Quality:
      combineLatest([
          this.store.select(state => state.player.qualities),
          this.store.select(state => state.player.currentQuality)
        ]
      ).pipe(
        filter(([qualities, currentQuality]) => qualities && qualities.length && currentQuality != null),
        take(1)
      )
        .subscribe(([qualities, currentQuality]) => mb.switchToQuality(currentQuality));

      this.proto.onUiEvent(ActionTypes.SELECT_QUALITY).subscribe(({quality}) => mb.switchToQuality(quality));
    }
  }

  setVolume(value: number) {
    this.playable.setVolume(value);
  }

  play() {
    if (!this.playable.isPlaying) {
      this.playable.play();
    } else {
      this.playable.stop();
    }
  }

  seekTo(position) {
    const seekable = this.playable as Seekable;
    if (seekable.isSeekable) {
      seekable.seek(position);
    }
  }

  seekToLive() {
    const seekable = this.playable as SeekableLive;
    if (seekable.isSeekable && seekable.isLive) {
      seekable.seekToLive();
    }
  }

  destroy() {
    this.proto.cleanUp();
  }


}
